import { Component, Prop } from 'vue-property-decorator'
import Vue from '@src/vue'
import { CreateElement } from 'vue'
import camelcaseKeys from 'camelcase-keys'

import './notifications.sass'
import {
  NotificationType,
  AffectedProduct,
  CommentParameters,
  ExerciseSupportTicket,
  ExerciseSupportTicketNotification,
  StateParameters
} from '@src/data/support-ticket'
import { noop } from '@src/util/noop'
import { Modal } from '@src/elements/modal/modal'
import { Icon } from '@src/elements/icon/icon'
import { API } from '@src/lib/api_path'
import ApiClient from '@src/lib/api_client'
import { convertToLocalDate, convertYYYYMMDDToDate } from '@src/util/date-format'
import { ExerciseResponse } from '@src/data/exercise-response'
import { BOTTOM_SCROLL_THRESHOLD } from '@src/util/constants'
import { Loader } from '@src/components/loading-screen/loader/loader'

const ITEMS_PER_PAGE = 20
@Component({
  computed: {
    availableExercises () {
      return this.$store.state.availableExercises
    }
  }
})
export class TicketNotification extends Vue {

  @Prop({ required: true })
  ticket: ExerciseSupportTicket

  @Prop({ required: false, default: false })
  unread: boolean

  @Prop({ required: false, default: noop })
  ticketSelectCallback: (ticket: ExerciseSupportTicket, notificationId: number) => void

  @Prop({ required: true })
  id: number

  @Prop({ required: true })
  createdAt: string

  @Prop({ required: true })
  notificationType: NotificationType

  @Prop({ required: false, default: {} })
  parameters: CommentParameters | StateParameters

  private availableExercises: ExerciseResponse[]

  render (h: CreateElement) {
    const exercise = this.loadExercise(this.ticket.details.exerciseId!)

    let formattedDate = convertToLocalDate(convertYYYYMMDDToDate(this.createdAt))

    return <div class={`ticket-notification${this.unread ? ' unread' : '' }`}
                onClick={() => this.ticketSelectCallback(this.ticket, this.id)}>
      <div class='title-wrapper'>
        {this.ticket.problemType === AffectedProduct.STATION_LEGACY ? <Icon name='brand/3d'/> : <Icon name='brand/2d'/>}
        <div class='title'>{exercise && exercise.name && exercise.name}</div>
      </div>
      <div class='body'>
        <div class='left-side'>
          <div class='date'>{formattedDate}</div>
        </div>
        <div class='right-side'>
          {this.notificationType === NotificationType.STATE_CHANGED &&
            <div class='state-change'>
              <div class='old-state'>{this.$t('SUPPORT_PAGE.SUPPORT_TICKET.' + (this.parameters as StateParameters).from.toUpperCase())}</div>
              <div class='new-state'>{this.$t('SUPPORT_PAGE.SUPPORT_TICKET.' + (this.parameters as StateParameters).to.toUpperCase())}</div>
            </div> }
          {this.notificationType === NotificationType.COMMENT_ADDED && <div class='comment'>{(this.parameters as CommentParameters).commentBody}</div>}
        </div>
      </div>
    </div>
  }

  private loadExercise (exerciseId: number): ExerciseResponse {
    return this.availableExercises.filter(exercise => exercise.id === exerciseId)[0] || null
  }
}

@Component({
  computed: {
    availableExercises () {
      return this.$store.state.availableExercises
    }
  }
})
export class Notifications extends Vue {

  @Prop({ required: false, default: noop })
  selectCallback: (ticket: ExerciseSupportTicket) => void

  private apiClient: ApiClient = new ApiClient()

  private notifications: ExerciseSupportTicketNotification[] = []

  private show: boolean = false

  private loading: boolean = true
  private currentPage: number = 1
  private noOfPages: number = 0
  private noOfUnreadNotifications: number = 0

  async mounted () {
    this.notifications = await this.fetchNotifications(1)
    console.log('notifications', this.notifications)
  }

  setVisibility (visibility: boolean) {
    this.show = visibility
  }

  ticketSelected (ticket: ExerciseSupportTicket, notificationId: number) {
    // in the future we will mark notifications as read via notificationId
    this.selectCallback(ticket)
    this.setVisibility(false)
    return this.markAsRead(notificationId).then(async () => {
      this.notifications = await this.fetchNotifications(1)
    })
  }

  render (h: CreateElement) {
    return <div class='notification-icon-wrapper'>

      <div class='bell-wrapper' onClick={() => this.setVisibility(true)}>
        <Icon name='util/bell'/>
        { this.noOfUnreadNotifications > 0 ? <div class='inner-circle'/> : null }
      </div>

      <Modal show={this.show} overflow='scrollable'
             closeCallback={() => this.setVisibility(false)}
             cssClass='notifications-modal'
             id='notifications-wrapper'
             scroll={this.onScroll}
      >
        <div slot='customTitle' class='title'>{this.$t('SUPPORT_PAGE.SUPPORT_TICKET.NOTIFICATIONS')}
          {this.noOfUnreadNotifications > 0 && ':'}
          {this.noOfUnreadNotifications > 0 && <div class='unread'>&nbsp;{this.noOfUnreadNotifications}&nbsp;{this.$t('SUPPORT_PAGE.SUPPORT_TICKET.UNREAD')}</div>}
        </div>
        {this.notifications.map((notification) =>
          <TicketNotification key={notification.id}
                              id={notification.id}
                              ticket={notification.supportTicket}
                              createdAt={notification.createdAt}
                              ticketSelectCallback={this.ticketSelected}
                              parameters={notification.parameters}
                              notificationType={notification.key}
                              unread={notification.unread}
          />
        )}
        {this.loading && <Loader/>}
      </Modal>
    </div>
  }

  private bottomOfPageReached () {
    const modalBody = document.getElementById('modal-body')
    if (!modalBody) {
      return false
    }

    return modalBody.scrollTop + modalBody.offsetHeight + BOTTOM_SCROLL_THRESHOLD >= modalBody.scrollHeight
  }

  private async onScroll () {
    if (this.bottomOfPageReached()) {
      if (this.currentPage < this.noOfPages && !this.loading) {
        this.currentPage++
        let nextPageNotifications = await this.fetchNotifications(this.currentPage)
        this.notifications = this.notifications.concat(nextPageNotifications)
      }
    }
  }

  private fetchNotifications (page: number): Promise<ExerciseSupportTicketNotification[]> {
    this.loading = true
    return this.apiClient.get<any>(API.TRAINERS_API.SUPPORT_TICKETS_NOTIFICATIONS
      .replace('{page}', page.toString())
      .replace('{itemsPerPage}', ITEMS_PER_PAGE.toString())
    )
      .then(response => {
        let notifications = response.activities.map((raw) => new ExerciseSupportTicketNotification((camelcaseKeys(raw))))
        this.noOfPages = response.meta.total_pages
        this.loading = false
        this.noOfUnreadNotifications = response.meta.total_unread_activities

        return notifications
      })
  }

  private markAsRead (id: number) {
    return this.apiClient.post<any>(API.TRAINERS_API.SUPPORT_TICKETS_NOTIFICATIONS_READ
      .replace('{id}', id.toString()), {})
      .catch((error) => {
        console.log(`Error while trying to mark as read notification with id ${id}`, error)
      })
  }
}
